import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import EnLayout from '@/components/feature/EnLayout';
import useSEO from '@/hooks/useSEO';

// ─────────────────────────────────────────────
// Shared components
// ─────────────────────────────────────────────
function Breadcrumb({ current }: { current: string }) {
  return (
    <div className="flex items-center gap-2 mb-4 text-white/60 text-[0.8125rem]">
      <Link to="/en" className="hover:text-white transition-colors cursor-pointer">Home</Link>
      <i className="ri-arrow-right-s-line" />
      <Link to="/en/services" className="hover:text-white transition-colors cursor-pointer">OEM/ODM Services</Link>
      <i className="ri-arrow-right-s-line" />
      <span className="text-white/90">{current}</span>
    </div>
  );
}

const SERVICE_SUBNAV_ITEMS = [
  { label: 'Best-Selling Formulas', path: '/en/services/hot-formulas' },
  { label: 'Natural Ingredients', path: '/en/services/natural-ingredients' },
  { label: 'Dosage Forms', path: '/en/services/dosage-forms' },
  { label: 'Packaging Design', path: '/en/services/packaging' },
];

function ServiceSubNav({ current }: { current: string }) {
  return (
    <section className="py-10 bg-[#f7faf7] border-t border-gray-100">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem]">Other Service Pages</h3>
          <Link
            to="/en/services"
            className="inline-flex items-center gap-2 text-[#4CAF50] hover:text-[#43a047] text-[0.9375rem] font-semibold transition-colors cursor-pointer group"
          >
            <i className="ri-arrow-left-line transition-transform duration-200 group-hover:-translate-x-0.5" />
            Back to Services Overview
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICE_SUBNAV_ITEMS.map((s) => (
            <Link
              key={s.path}
              to={s.path}
              className={`bg-white rounded-xl p-5 border transition-all duration-200 cursor-pointer block hover:-translate-y-0.5 ${
                current === s.path ? 'border-[#4CAF50] bg-green-50' : 'border-gray-100 hover:border-green-200'
              }`}
            >
              <p className={`font-bold text-[0.9375rem] ${current === s.path ? 'text-[#4CAF50]' : 'text-[#1a2e1a]'}`}>{s.label}</p>
              {current === s.path && <p className="text-[#4CAF50] text-[0.75rem] mt-1">Current page</p>}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
// Hot Formulas Page
// ─────────────────────────────────────────────
const formulas = [
  {
    id: 1, icon: 'ri-heart-line', title: 'Natural Beauty', color: '#f48fb1',
    desc: 'Collagen, hyaluronic acid, and vitamin C as core natural ingredients, maintaining radiant and moisturized skin from within.',
    tags: ['Collagen', 'Hyaluronic Acid', 'Vitamin C', 'Plant Extract'],
    dosages: ['Powder', 'Jelly Stick', 'Soft Gel'],
    img: 'https://readdy.ai/api/search-image?query=natural%20beauty%20supplement%20collagen%20hyaluronic%20acid%20skincare%20product%20with%20rose%20petals%20white%20background%20elegant%20feminine%20packaging%20health%20beauty%20photography&width=400&height=300&seq=hf-beauty-01&orientation=landscape',
  },
  {
    id: 2, icon: 'ri-sparkling-line', title: "Women\'s Intimate Health", color: '#ce93d8',
    desc: 'Combining lactobacillus and cranberry extract to maintain a healthy intimate environment for women, providing comfort and confidence.',
    tags: ['Lactobacillus', 'Cranberry', 'Probiotics', 'Feminine Care'],
    dosages: ['Capsule', 'Powder'],
    img: 'https://readdy.ai/api/search-image?query=feminine%20health%20supplement%20probiotic%20cranberry%20extract%20product%20soft%20pastel%20purple%20pink%20background%20elegant%20women%20health%20care%20packaging&width=400&height=300&seq=hf-fem-01&orientation=landscape',
  },
  {
    id: 3, icon: 'ri-men-line', title: "Men\'s Vitality", color: '#90caf9',
    desc: 'Premium oyster extract, maca, and zinc to support male vitality and physical performance from within.',
    tags: ['Oyster Extract', 'Maca', 'Zinc', 'Vitality Formula'],
    dosages: ['Capsule', 'Tablet'],
    img: 'https://readdy.ai/api/search-image?query=mens%20health%20supplement%20maca%20oyster%20extract%20zinc%20vitality%20product%20dark%20masculine%20packaging%20on%20clean%20white%20background%20professional%20health%20photography&width=400&height=300&seq=hf-men-01&orientation=landscape',
  },
  {
    id: 4, icon: 'ri-leaf-line', title: 'Digestive Lightness', color: '#a5d6a7',
    desc: 'Probiotics, dietary fiber, and enzyme complex formula to maintain gut microbiome balance for a lighter, more comfortable daily life.',
    tags: ['Probiotics', 'Dietary Fiber', 'Digestive Enzymes', 'Gut Health'],
    dosages: ['Powder', 'Capsule', 'Jelly Stick'],
    img: 'https://readdy.ai/api/search-image?query=digestive%20health%20probiotic%20fiber%20supplement%20green%20fresh%20natural%20ingredients%20gut%20health%20product%20on%20light%20background%20clean%20minimal%20photography&width=400&height=300&seq=hf-digest-01&orientation=landscape',
  },
  {
    id: 5, icon: 'ri-eye-line', title: 'Eye Clarity', color: '#80deea',
    desc: 'Lutein, zeaxanthin, and blueberry extract for comprehensive eye health protection — ideal for those who spend long hours on screens.',
    tags: ['Lutein', 'Zeaxanthin', 'Blueberry Extract', 'Eye Care'],
    dosages: ['Soft Gel', 'Tablet'],
    img: 'https://readdy.ai/api/search-image?query=eye%20health%20supplement%20lutein%20zeaxanthin%20blueberry%20extract%20product%20on%20light%20background%20healthy%20vision%20care%20product%20photography%20natural%20light&width=400&height=300&seq=hf-eye-01&orientation=landscape',
  },
  {
    id: 6, icon: 'ri-riding-line', title: 'Sports Performance', color: '#ffe082',
    desc: 'BCAA, creatine, and protein for sports nutrition to support muscle recovery and enhance athletic performance.',
    tags: ['BCAA', 'Creatine', 'Protein', 'Sports Nutrition'],
    dosages: ['Powder', 'Capsule', 'Tablet'],
    img: 'https://readdy.ai/api/search-image?query=sports%20nutrition%20supplement%20BCAA%20protein%20powder%20workout%20fitness%20product%20on%20dark%20sporty%20background%2C%20health%20bodybuilding%20supplement%20professional%20photography&width=400&height=300&seq=hf-sport-01&orientation=landscape',
  },
  {
    id: 7, icon: 'ri-moon-line', title: 'Deep Sleep Formula', color: '#b39ddb',
    desc: 'Natural L-theanine, GABA, and valerian extract to promote relaxation and improve sleep quality.',
    tags: ['L-Theanine', 'GABA', 'Valerian', 'Sleep Support'],
    dosages: ['Capsule', 'Powder', 'Jelly Stick'],
    img: 'https://readdy.ai/api/search-image?query=sleep%20supplement%20GABA%20valerian%20L-theanine%20natural%20sleep%20aid%20product%20lavender%20purple%20background%20calm%20relaxation%20health%20photography&width=400&height=300&seq=hf-sleep-01&orientation=landscape',
  },
  {
    id: 8, icon: 'ri-run-line', title: 'Bone & Joint Support', color: '#bcaaa4',
    desc: 'Calcium, magnesium, vitamin D3, and glucosamine for strengthening bones and joints — ideal for middle-aged and senior consumers.',
    tags: ['Calcium & Magnesium', 'Vitamin D3', 'Glucosamine', 'Bone Health'],
    dosages: ['Tablet', 'Capsule'],
    img: 'https://readdy.ai/api/search-image?query=bone%20joint%20health%20supplement%20calcium%20magnesium%20vitamin%20D%20glucosamine%20product%20on%20white%20background%2C%20strong%20healthy%20bones%20concept%20photography&width=400&height=300&seq=hf-bone-01&orientation=landscape',
  },
  {
    id: 9, icon: 'ri-seedling-line', title: 'Hair Vitality', color: '#8d6e63',
    desc: 'Biotin, he shou wu extract, and black sesame to support hair health from root to tip.',
    tags: ['Biotin', 'He Shou Wu', 'Black Sesame', 'Hair Health'],
    dosages: ['Capsule', 'Powder', 'Jelly Stick'],
    img: 'https://readdy.ai/api/search-image?query=hair%20health%20supplement%20biotin%20black%20sesame%20he%20shou%20wu%20product%20with%20natural%20herbs%20on%20clean%20white%20background%20elegant%20hair%20care%20product%20photography&width=400&height=300&seq=hf-hair-01&orientation=landscape',
  },
  {
    id: 10, icon: 'ri-emotion-happy-line', title: "Children\'s Growth", color: '#ffcc80',
    desc: 'Calcium, vitamin D, DHA, and probiotics formulated for children — safe, great-tasting, and supporting all-round development.',
    tags: ['Calcium', 'DHA', 'Probiotics', 'Child Safe'],
    dosages: ['Powder', 'Chewable Tablet', 'Jelly Stick'],
    img: 'https://readdy.ai/api/search-image?query=children%20health%20supplement%20calcium%20DHA%20probiotic%20colorful%20fruit%20flavored%20vitamins%20for%20kids%20on%20bright%20cheerful%20background%2C%20safe%20natural%20ingredients&width=400&height=300&seq=hf-kids-01&orientation=landscape',
  },
  {
    id: 11, icon: 'ri-heart-pulse-line', title: 'Baby Formula', color: '#f8bbd0',
    desc: 'Exclusively formulated for infants with the gentlest natural ingredients, supporting gut health and immune development.',
    tags: ['Infant Probiotics', 'Colostrum', 'Vitamins', 'Gentle Formula'],
    dosages: ['Powder', 'Drops'],
    img: 'https://readdy.ai/api/search-image?query=baby%20infant%20health%20supplement%20probiotic%20powder%20gentle%20natural%20formula%20for%20newborn%20toddler%20on%20soft%20pastel%20pink%20white%20background%20gentle%20photography&width=400&height=300&seq=hf-baby-01&orientation=landscape',
  },
  {
    id: 12, icon: 'ri-scales-3-line', title: 'Weight Management', color: '#c8e6c9',
    desc: 'Garcinia cambogia, white kidney bean, and green coffee extract for natural weight management — best paired with a healthy lifestyle.',
    tags: ['Garcinia Cambogia', 'White Kidney Bean', 'Green Coffee', 'Weight Management'],
    dosages: ['Capsule', 'Powder'],
    img: 'https://readdy.ai/api/search-image?query=weight%20management%20slim%20supplement%20green%20coffee%20white%20kidney%20bean%20garcinia%20cambogia%20product%20on%20clean%20fresh%20green%20white%20background%20healthy%20slimming%20photography&width=400&height=300&seq=hf-slim-01&orientation=landscape',
  },
  {
    id: 13, icon: 'ri-sun-line', title: 'Daily Nutrition', color: '#fff59d',
    desc: 'Comprehensive multivitamins and minerals designed for modern diets, covering all daily essential nutritional needs.',
    tags: ['Multivitamins', 'Minerals', 'Antioxidants', 'Daily Health'],
    dosages: ['Tablet', 'Capsule', 'Powder'],
    img: 'https://readdy.ai/api/search-image?query=multivitamin%20daily%20nutrition%20supplement%20colorful%20capsules%20tablets%20minerals%20vitamins%20on%20white%20background%20fresh%20healthy%20lifestyle%20concept%20photography&width=400&height=300&seq=hf-daily-01&orientation=landscape',
  },
  {
    id: 14, icon: 'ri-bear-smile-line', title: 'Pet Health', color: '#bcaaa4',
    desc: 'Pet health formulas designed for dogs and cats, compliant with feed additive regulations — keeping your furry friends healthy.',
    tags: ['Pet Probiotics', 'Omega-3', 'Joint Care', 'Feed Regulation'],
    dosages: ['Powder', 'Treat Format'],
    img: 'https://readdy.ai/api/search-image?query=pet%20health%20supplement%20dog%20cat%20natural%20organic%20probiotic%20omega3%20joint%20care%20product%20with%20cute%20pet%20imagery%20on%20light%20background%20natural%20health%20photography&width=400&height=300&seq=hf-pet-01&orientation=landscape',
  },
  {
    id: 15, icon: 'ri-mental-health-line', title: 'Focus & Calm', color: '#b2dfdb',
    desc: 'Phosphatidylserine, DHA, and B vitamins to support concentration and emotional stability — perfect for busy professionals.',
    tags: ['Phosphatidylserine', 'DHA', 'Vitamin B Complex', 'Emotional Support'],
    dosages: ['Soft Gel', 'Capsule'],
    img: 'https://readdy.ai/api/search-image?query=brain%20health%20focus%20supplement%20phosphatidylserine%20DHA%20B%20vitamins%20cognitive%20function%20product%20on%20calm%20blue%20green%20background%20mental%20wellness%20photography&width=400&height=300&seq=hf-brain-01&orientation=landscape',
  },
  {
    id: 16, icon: 'ri-shield-star-line', title: 'Immune Defense', color: '#a5d6a7',
    desc: 'Vitamin C, zinc, elderberry extract, and lactoferrin for comprehensive immune system reinforcement.',
    tags: ['Vitamin C', 'Zinc', 'Elderberry', 'Immune Boost'],
    dosages: ['Tablet', 'Powder', 'Capsule'],
    img: 'https://readdy.ai/api/search-image?query=immune%20health%20supplement%20vitamin%20C%20zinc%20elderberry%20lactoferrin%20shield%20protection%20product%20on%20fresh%20green%20white%20background%20healthy%20immunity%20photography&width=400&height=300&seq=hf-immune-01&orientation=landscape',
  },
  {
    id: 17, icon: 'ri-bug-2-line', title: 'Probiotic Power', color: '#ffe0b2',
    desc: 'Multi-strain probiotics with prebiotics for comprehensive gut microbiome support and healthy digestive function.',
    tags: ['Multi-Strain Probiotics', 'Prebiotics', 'Lactobacillus', 'Gut Microbiome'],
    dosages: ['Powder', 'Capsule', 'Jelly Stick'],
    img: 'https://readdy.ai/api/search-image?query=probiotic%20supplement%20multiple%20strains%20lactobacillus%20product%20with%20clean%20fresh%20natural%20background%20white%20green%20gut%20microbiome%20health%20photography&width=400&height=300&seq=hf-probiotic-01&orientation=landscape',
  },
];

function HotFormulasPage() {
  const [expanded, setExpanded] = useState<number | null>(null);
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=diverse%20collection%20of%20colorful%20health%20supplement%20products%20capsules%20powders%20gummies%20arranged%20beautifully%20on%20white%20marble%20surface%2C%20vibrant%20natural%20ingredients%20visible%2C%20professional%20product%20display%20photography&width=1920&height=420&seq=hf-hero-bg-01&orientation=landscape"
            alt="Best-Selling Formulas"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/65 to-[#1a3a1a]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb current="Best-Selling Formulas" />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              OEM/ODM Services
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              Best-Selling Formulas
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              Market-tested formulas tailored for different demographics, lifestyles and trends — helping your brand find the right formula direction.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-b border-gray-100 py-5">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { num: '17+', label: 'Formula Categories' },
              { num: '1,000+', label: 'OEM Items Produced' },
              { num: '7', label: 'Dosage Form Types' },
              { num: '250+', label: 'Brand Partners' },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-[#4CAF50] font-bold text-[1.75rem] md:text-[2rem] leading-none mb-1">{s.num}</p>
                <p className="text-gray-500 text-[0.875rem]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formula grid */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Formula Overview
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              17 Formula Directions
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              Click on a formula card to expand details — find the direction that best fits your brand positioning
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {formulas.map((f) => (
              <div
                key={f.id}
                className={`bg-white rounded-2xl overflow-hidden border transition-all duration-300 cursor-pointer ${
                  expanded === f.id ? 'border-[#4CAF50]/50' : 'border-gray-100 hover:border-green-200 hover:-translate-y-1'
                }`}
                onClick={() => setExpanded(expanded === f.id ? null : f.id)}
              >
                <div className="w-full h-[240px] overflow-hidden relative">
                  <img src={f.img} alt={f.title} className="w-full h-full object-cover object-top transition-transform duration-300 hover:scale-105" />
                  <div className="absolute top-3 right-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ backgroundColor: `${f.color}30` }}>
                      <i className={`${f.icon} text-sm`} style={{ color: f.color }} />
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem]">{f.title}</h3>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-all duration-200 flex-shrink-0 ${expanded === f.id ? 'bg-[#4CAF50] text-white' : 'bg-green-50 text-[#4CAF50]'}`}>
                      <i className={`text-xs ${expanded === f.id ? 'ri-subtract-line' : 'ri-add-line'}`} />
                    </div>
                  </div>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-3 line-clamp-2">{f.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {f.dosages.map((d) => (
                      <span key={d} className="bg-green-50 border border-green-100 text-[#4CAF50] text-[0.75rem] px-2.5 py-1 rounded-full font-medium">{d}</span>
                    ))}
                  </div>
                </div>
                {expanded === f.id && (
                  <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                    <p className="text-gray-600 text-[0.875rem] leading-[1.75] mb-3">{f.desc}</p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {f.tags.map((tag) => (
                        <span key={tag} className="bg-[#f7faf7] border border-gray-200 text-gray-500 text-[0.75rem] px-2.5 py-1 rounded-full">{tag}</span>
                      ))}
                    </div>
                    <Link
                      to="/en/contact/form"
                      onClick={(e) => e.stopPropagation()}
                      className="whitespace-nowrap inline-flex items-center gap-1.5 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.8125rem] px-4 py-2 rounded-full transition-all duration-200 cursor-pointer"
                    >
                      Inquire About This Formula
                      <i className="ri-arrow-right-line text-xs" />
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            Can&apos;t Find the Right Formula Direction?
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            Kang Yong Biotech can tailor a completely new formula direction based on your brand positioning and target demographic. Contact our R&D team today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              Inquire About Formula Development
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/en/services/dosage-forms" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              Explore Dosage Forms
            </Link>
          </div>
        </div>
      </section>
      <ServiceSubNav current="/en/services/hot-formulas" />
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-5">Related Pages</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: 'Natural Ingredients', desc: 'Animal, plant, mushroom, algae — explore our diverse ingredient categories.', href: '/en/services/natural-ingredients' },
              { title: 'Dosage Forms', desc: 'Capsule, tablet, powder, jelly stick — choose the ideal format for your brand.', href: '/en/services/dosage-forms' },
              { title: 'Contact Us', desc: 'Fill out our inquiry form and let Kang Yong help you build your winning brand.', href: '/en/contact/form' },
            ].map((r) => (
              <Link key={r.href} to={r.href} className="block bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer group">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5 group-hover:text-[#4CAF50] transition-colors">{r.title}</p>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{r.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────
// Natural Ingredients Page
// ─────────────────────────────────────────────
const categories = [
  {
    id: 'animal', icon: 'ri-anchor-line', title: 'Animal-Derived', color: '#ef9a9a',
    intro: 'High bioavailability raw materials from marine and land animals, rich in essential amino acids, proteins, and bioactive compounds for superior absorption.',
    highlights: ['High Bioavailability', 'Rich in Essential Amino Acids', 'Natural Bioactive Compounds'],
    ingredients: [
      { name: 'Fish Collagen', desc: 'Extracted from fish skin and scales. Small molecular weight for fast absorption — the core ingredient for beauty formulas.' },
      { name: 'Fish Oil (Omega-3)', desc: 'Rich in EPA and DHA, supporting cardiovascular and brain health. Highly concentrated TG form for better absorption.' },
      { name: 'Oyster Extract', desc: 'A premium natural source of zinc and amino acids — a common core ingredient in men\'s health formulas.' },
      { name: 'Whey Protein', desc: 'A complete protein for fast absorption, the primary ingredient in sports recovery and muscle maintenance formulas.' },
      { name: 'Colostrum', desc: 'Rich in immunoglobulins and growth factors, supporting immune systems in both infants and adults.' },
    ],
    img: 'https://readdy.ai/api/search-image?query=animal%20derived%20natural%20supplement%20ingredients%20fish%20collagen%20marine%20omega3%20oyster%20extract%20on%20clean%20white%20background%2C%20professional%20health%20ingredient%20photography%20natural%20light&width=600&height=380&seq=ni-animal-01&orientation=landscape',
  },
  {
    id: 'plant', icon: 'ri-plant-line', title: 'Plant-Derived', color: '#4CAF50',
    intro: 'Covering roots, stems, leaves, and fruit extracts from around the world, offering diverse functional support aligned with modern consumers\' preference for natural ingredients.',
    highlights: ['Natural Plant Extracts', 'Diverse Benefits', 'Consumer Preferred'],
    ingredients: [
      { name: 'Curcumin (Turmeric)', desc: 'The active compound in turmeric root, with powerful antioxidant properties — a top ingredient in anti-inflammatory formulas.' },
      { name: 'Cranberry Extract', desc: 'Rich in proanthocyanidins (PACs), the hallmark botanical for women\'s intimate health.' },
      { name: 'Lutein', desc: 'Extracted from marigold petals — the most important core ingredient in eye care formulas.' },
      { name: 'Elderberry Extract', desc: 'Rich in anthocyanins and immune-supporting properties. One of the fastest-growing ingredients globally.' },
      { name: 'Maca Extract', desc: 'A Peruvian highland plant providing natural support for men\'s vitality and physical stamina.' },
      { name: 'Garcinia Cambogia', desc: 'Contains HCA (hydroxycitric acid), a commonly used plant ingredient in weight management formulas.' },
    ],
    img: 'https://readdy.ai/api/search-image?query=plant%20botanical%20extract%20natural%20ingredients%20turmeric%20ginger%20herbs%20green%20tea%20extract%20on%20white%20wooden%20surface%2C%20health%20supplement%20raw%20materials%2C%20clean%20minimal%20photography&width=600&height=380&seq=ni-plant-01&orientation=landscape',
  },
  {
    id: 'mushroom', icon: 'ri-seedling-line', title: 'Mushroom-Derived', color: '#8d6e63',
    intro: 'Functional mushrooms contain unique bioactive compounds such as polysaccharides and triterpenes. Long used in Eastern traditional health practices, with ongoing modern scientific validation.',
    highlights: ['Rich in Polysaccharides', 'Eastern Traditional Wisdom', 'Multi-Functional'],
    ingredients: [
      { name: 'Reishi Extract', desc: 'Rich in triterpenes and polysaccharides. A premium mushroom for immune modulation and antioxidant formulas.' },
      { name: 'Antrodia Cinnamomea (Taiwan Native)', desc: 'Taiwan\'s unique and precious mushroom. Rich in triterpenes and a high-value ingredient for premium formulas.' },
      { name: 'Maitake Extract', desc: 'D-Fraction polysaccharides, commonly used in blood sugar management and immune support formulas.' },
      { name: 'Cordyceps Extract', desc: 'A prized traditional health ingredient. Suitable for anti-fatigue formulas in premium health brands.' },
    ],
    img: 'https://readdy.ai/api/search-image?query=functional%20mushroom%20supplement%20reishi%20ganoderma%20lion%20mane%20extract%20natural%20health%20product%20on%20dark%20wood%20background%2C%20traditional%20Chinese%20medicine%20herbs%20wellness%20photography&width=600&height=380&seq=ni-mushroom-01&orientation=landscape',
  },
  {
    id: 'algae', icon: 'ri-water-flash-line', title: 'Algae & Oils', color: '#26a69a',
    intro: 'Marine algae are excellent natural sources of DHA, EPA, and various micronutrients — suitable for vegetarians and representing a sustainable, eco-friendly ingredient choice.',
    highlights: ['Vegan Friendly', 'Marine Sustainable', 'High-Purity DHA'],
    ingredients: [
      { name: 'Algae DHA', desc: 'Plant-based DHA extracted from microalgae. Suitable for vegetarian and infant formulas.' },
      { name: 'Spirulina', desc: 'A blue-green algae with ultra-high protein content and multiple vitamins and minerals — an excellent plant-based protein source.' },
      { name: 'Chlorella', desc: 'Rich in chlorophyll and vitamin B12, a popular ingredient for detoxification and daily supplementation.' },
      { name: 'Astaxanthin', desc: 'Extracted from Haematococcus algae. Extremely powerful antioxidant — a high-performance ingredient for eye care and beauty formulas.' },
    ],
    img: 'https://readdy.ai/api/search-image?query=algae%20oil%20DHA%20spirulina%20chlorella%20astaxanthin%20marine%20plant%20based%20supplement%20ingredient%20on%20teal%20blue%20green%20background%2C%20sustainable%20ocean%20health%20product%20photography&width=600&height=380&seq=ni-algae-01&orientation=landscape',
  },
  {
    id: 'biochem', icon: 'ri-flask-line', title: 'Biochemical Compounds', color: '#5c6bc0',
    intro: 'High-purity bioactive substances produced through biochemical processes — clearly defined efficacy, stable content, and indispensable for functional health supplement formulas.',
    highlights: ['High Purity', 'Stable Content', 'Defined Efficacy'],
    ingredients: [
      { name: 'Coenzyme Q10 (CoQ10)', desc: 'A key cofactor in mitochondrial energy metabolism. The core ingredient in cardiovascular health and anti-fatigue formulas.' },
      { name: 'GABA (γ-Aminobutyric Acid)', desc: 'A natural neurotransmitter — the primary active compound in sleep and relaxation formulas.' },
      { name: 'NMN (Nicotinamide Mononucleotide)', desc: 'A precursor to NAD+. A popular premium ingredient in high-end anti-aging formulas.' },
      { name: 'L-Theanine', desc: 'A natural amino acid from tea. Promotes relaxation without drowsiness; paired with caffeine for enhanced focus.' },
      { name: 'Glucosamine & Chondroitin', desc: 'The gold combination for joint cartilage repair — an essential ingredient pairing for bone and joint health formulas.' },
    ],
    img: 'https://readdy.ai/api/search-image?query=biochemical%20supplement%20CoQ10%20NMN%20GABA%20active%20compound%20laboratory%20research%20high%20purity%20extract%20on%20clean%20scientific%20minimal%20background%2C%20health%20biotech%20ingredient%20photography&width=600&height=380&seq=ni-biochem-01&orientation=landscape',
  },
  {
    id: 'other', icon: 'ri-star-line', title: 'Other Nutritional Sources', color: '#ff9800',
    intro: 'Vitamins, minerals, probiotics, and dietary fiber are the foundational building blocks of health supplements. Kang Yong selects high-quality raw materials that meet specifications, ensuring batch-consistent content.',
    highlights: ['Essential Basics', 'Consistent Content', 'Wide Selection'],
    ingredients: [
      { name: 'Vitamins C, D3, E, B Complex', desc: 'Water-soluble and fat-soluble vitamin series — essential building blocks for multivitamin formulas.' },
      { name: 'Calcium, Magnesium, Zinc, Iron', desc: 'Mineral series in organic or inorganic salt forms depending on formula needs, ensuring optimal absorption.' },
      { name: 'Multi-Strain Probiotics', desc: 'Lactobacillus, Bifidobacterium, and other multi-strain combinations flexibly selected based on formula efficacy targets.' },
      { name: 'Chicory Fiber (FOS/Inulin)', desc: 'Natural prebiotic feeding gut-friendly bacteria. Synergistic effect when combined with probiotics.' },
      { name: 'Digestive Enzyme Complex', desc: 'Protease, amylase, lipase combination to assist digestion and absorption — commonly used in gut health formulas.' },
    ],
    img: 'https://readdy.ai/api/search-image?query=vitamins%20minerals%20probiotic%20dietary%20fiber%20supplement%20ingredients%20colorful%20assortment%20on%20clean%20white%20background%2C%20essential%20nutrition%20daily%20supplement%20product%20photography&width=600&height=380&seq=ni-other-01&orientation=landscape',
  },
];

function NaturalIngredientsPage() {
  const [activeId, setActiveId] = useState<string | null>(null);
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=diverse%20natural%20raw%20ingredients%20herbs%20botanical%20extracts%20fish%20oil%20mushrooms%20algae%20vitamins%20arranged%20on%20clean%20white%20marble%20surface%2C%20health%20supplement%20raw%20material%20sourcing%20professional%20photography&width=1920&height=420&seq=ni-hero-bg-01&orientation=landscape"
            alt="Natural Ingredients"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/65 to-[#1a3a1a]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb current="Natural Ingredients" />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">OEM/ODM Services</span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">Natural Ingredients</h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              From animal, plant, mushroom, and algae to biochemical nutritional sources — select the right ingredient direction for your product.
            </p>
          </div>
        </div>
      </section>

      {/* Category filter */}
      <section className="py-6 bg-white border-b border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setActiveId(null)} className={`whitespace-nowrap px-4 py-2 rounded-full text-[0.875rem] font-medium transition-all duration-200 cursor-pointer ${activeId === null ? 'bg-[#4CAF50] text-white' : 'bg-[#f7faf7] text-gray-600 border border-gray-200 hover:border-green-300 hover:text-[#4CAF50]'}`}>All Categories</button>
            {categories.map((cat) => (
              <button key={cat.id} onClick={() => setActiveId(cat.id === activeId ? null : cat.id)} className={`whitespace-nowrap px-4 py-2 rounded-full text-[0.875rem] font-medium transition-all duration-200 cursor-pointer ${activeId === cat.id ? 'bg-[#4CAF50] text-white' : 'bg-[#f7faf7] text-gray-600 border border-gray-200 hover:border-green-300 hover:text-[#4CAF50]'}`}>{cat.title}</button>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 space-y-8">
          {categories.filter((c) => activeId === null || c.id === activeId).map((cat) => (
            <div key={cat.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="flex flex-col lg:flex-row">
                <div className="w-full lg:w-[360px] flex-shrink-0">
                  <img src={cat.img} alt={cat.title} className="w-full h-[260px] lg:h-full object-cover object-top" />
                </div>
                <div className="flex-1 p-7">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${cat.color}20` }}>
                      <i className={`${cat.icon} text-xl`} style={{ color: cat.color }} />
                    </div>
                    <h2 className="text-[#1a2e1a] font-bold text-[1.375rem]">{cat.title}</h2>
                  </div>
                  <p className="text-gray-600 text-[1rem] leading-[1.75] mb-4">{cat.intro}</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {cat.highlights.map((h) => (
                      <span key={h} className="text-[0.8125rem] font-medium px-3 py-1 rounded-full border" style={{ color: cat.color, borderColor: `${cat.color}40`, backgroundColor: `${cat.color}10` }}>{h}</span>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {cat.ingredients.map((ing) => (
                      <div key={ing.name} className="flex items-start gap-3 bg-[#f7faf7] rounded-xl p-3.5">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-fill text-[#4CAF50] text-base" />
                        </div>
                        <div>
                          <p className="text-[#1a2e1a] font-semibold text-[0.875rem] mb-0.5">{ing.name}</p>
                          <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{ing.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quality assurance */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">Ingredient Quality Control</span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] mb-3">Every Batch, Strictly Inspected</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto">Kang Yong Biotech implements rigorous incoming inspection procedures for all raw materials</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { icon: 'ri-test-tube-line', title: 'Pesticide Residue Testing', desc: 'Multi-panel pesticide residue screening on every incoming batch' },
              { icon: 'ri-drop-line', title: 'Heavy Metal Analysis', desc: 'Comprehensive testing for lead, mercury, arsenic, cadmium and other heavy metals' },
              { icon: 'ri-bug-line', title: 'Microbiology Testing', desc: 'Total plate count, E. coli, and other microbiological safety confirmations' },
              { icon: 'ri-file-check-line', title: 'Active Content Verification', desc: 'Confirming active ingredient content meets specification sheet standards' },
            ].map((item) => (
              <div key={item.title} className="bg-[#f7faf7] rounded-2xl p-5 text-center border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl mx-auto mb-3 border border-green-100">
                  <i className={`${item.icon} text-[#4CAF50] text-xl`} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5">{item.title}</h3>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">Want to Know About Ingredient Compliance & Pricing?</h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">Kang Yong Biotech can help you evaluate ingredient feasibility and provide ingredient selection recommendations compliant with Taiwan regulations.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              Inquire About Ingredient Planning
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/en/services/hot-formulas" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              Browse Best-Selling Formulas
            </Link>
          </div>
        </div>
      </section>
      <ServiceSubNav current="/en/services/natural-ingredients" />
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-5">Related Pages</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: 'Best-Selling Formulas', desc: 'Explore 17 market-proven formula directions — find your brand\'s best starting point.', href: '/en/services/hot-formulas' },
              { title: 'Dosage Forms', desc: 'Capsule, tablet, powder, jelly stick — choose the format that best showcases your ingredients.', href: '/en/services/dosage-forms' },
              { title: 'Inquire About ODM Cooperation', desc: 'Fill out our inquiry form and let Kang Yong help you plan the ideal ingredient combination.', href: '/en/contact/form' },
            ].map((r) => (
              <Link key={r.href} to={r.href} className="block bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer group">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5 group-hover:text-[#4CAF50] transition-colors">{r.title}</p>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{r.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────
// Dosage Forms Page
// ─────────────────────────────────────────────
const dosageForms = [
  {
    id: 'powder', icon: 'ri-contrast-drop-line', title: 'Powder', subtitle: 'Fast Dissolving, Intuitive Intake', color: '#fff59d', colorDark: '#f9a825',
    desc: 'Powder dosage forms are ground or granulated for direct mixing into water or food. Ideal for formulas requiring higher daily intake volumes or fast absorption.',
    process: 'Ingredient blending & milling → Sieving & standardization → Filling & packaging (jar or stick pack) → QC sealing',
    suitable: ['Protein Supplements', 'Probiotics', 'Vitamin Complexes', "Children\'s Nutrition", 'Sports Nutrition'],
    advantages: ['Flexible serving size adjustments', 'Easy to add flavorings', 'Suitable for high-volume supplementation', 'Convenient instant mixing'],
    note: 'Powder is susceptible to moisture. Packaging must include special moisture-proof design, and storage in low-temperature, low-humidity environments is recommended.',
    img: 'https://readdy.ai/api/search-image?query=powder%20supplement%20scoop%20protein%20nutrition%20powder%20white%20powder%20in%20glass%20jar%20with%20spoon%20on%20clean%20white%20marble%20surface%2C%20health%20supplement%20product%20photography%20minimal&width=600&height=380&seq=df-powder-01&orientation=landscape',
  },
  {
    id: 'tablet', icon: 'ri-medicine-bottle-line', title: 'Tablet', subtitle: 'Convenient to Carry, Precise Dosage', color: '#c8e6c9', colorDark: '#2e7d32',
    desc: 'Tablets are compression-formed for a fixed appearance and stable dosage, making them the most widely used health supplement dosage form. Available in multiple shapes with optional coating to improve appearance and taste.',
    process: 'Ingredient blending → Granulation → Compression molding → QC (hardness/disintegration) → Coating (optional) → Packaging',
    suitable: ['Multivitamins', 'Minerals', 'Bone & Joint Health', 'Antioxidants', 'Daily Supplementation'],
    advantages: ['High production efficiency, competitive cost', 'Easy to carry and store', 'Precise and stable dosage', 'Optional coating to control disintegration rate'],
    note: 'Some ingredients are not compatible with high-temperature compression. Heat stability of raw materials must be confirmed during formula design.',
    img: 'https://readdy.ai/api/search-image?query=supplement%20tablets%20pills%20various%20colors%20shapes%20round%20oval%20on%20white%20background%2C%20pharmaceutical%20health%20product%20close%20up%20photography%2C%20clean%20minimal%20style&width=600&height=380&seq=df-tablet-01&orientation=landscape',
  },
  {
    id: 'capsule', icon: 'ri-capsule-line', title: 'Capsule', subtitle: 'Excellent Taste Masking, High Consumer Acceptance', color: '#b2dfdb', colorDark: '#00695c',
    desc: 'Capsules come in hard capsules (for powder or granules) and soft gels (for oil-based ingredients). One of the most accepted dosage forms in the market, effectively masking bitter or unpleasant ingredient odors.',
    process: 'Ingredient filling → Capsule joining → Polishing & sorting → QC visual/machine inspection → Packaging',
    suitable: ['Plant Extracts', 'Fish Oil / Evening Primrose Oil', 'Probiotics', 'Beauty Formulas', "Men\'s & Women\'s Health"],
    advantages: ['Effectively masks ingredient odors', 'Vegetarian or gelatin capsule options', 'Attractive appearance and easy identification', 'Preferred form for oil-based ingredients'],
    note: 'Soft gel manufacturing is more complex than hard capsules with slightly longer lead times. Vegetarian capsules appeal to vegan consumers as a differentiation selling point.',
    img: 'https://readdy.ai/api/search-image?query=capsule%20supplement%20hard%20soft%20gel%20capsules%20green%20white%20transparent%20close%20up%20scattered%20on%20white%20background%2C%20health%20supplement%20pharmaceutical%20product%20photography&width=600&height=380&seq=df-capsule-01&orientation=landscape',
  },
  {
    id: 'jelly', icon: 'ri-drop-line', title: 'Jelly Stick', subtitle: 'Great Taste, Broad Market Acceptance', color: '#f8bbd0', colorDark: '#ad1457',
    desc: 'Jelly sticks use a gel base to encapsulate active ingredients. Consumed directly by biting or squeezing — no water needed. Popular with children, seniors, and convenience-seeking consumers.',
    process: 'Solution preparation → Active ingredient addition → Mold/pouch filling → Cooling & setting → Sealing & packaging',
    suitable: ['Collagen', 'Probiotics', "Children\'s Vitamins", 'Sleep Formulas', 'Hair Health'],
    advantages: ['No water needed for consumption', 'Great taste with many flavor options', 'Suitable for children and seniors', 'Highly differentiated product format'],
    note: 'Jelly sticks have higher water content, resulting in shorter shelf life compared to powder or tablets. Preservative and packaging design require special attention.',
    img: 'https://readdy.ai/api/search-image?query=health%20supplement%20jelly%20stick%20gel%20collagen%20beauty%20product%20individual%20packet%20stick%20pouch%20on%20light%20background%2C%20fresh%20colorful%20fruit%20flavored%20health%20jelly%20product%20photography&width=600&height=380&seq=df-jelly-01&orientation=landscape',
  },
  {
    id: 'tea', icon: 'ri-cup-line', title: 'Tea Bag', subtitle: 'Natural Feel, Instant Brew', color: '#d7ccc8', colorDark: '#4e342e',
    desc: 'Tea bag formats fill dried botanical materials or powder into filter paper bags, preserving the natural appearance and aroma of plants. Ideal for brands positioning around "natural" or "herbal" themes with strong market differentiation.',
    process: 'Raw material drying & milling → Blending → Tea bag filling → Heat sealing → Outer bag packaging',
    suitable: ['Herbal Wellness Tea', 'Slimming Tea', 'Sleep Tea', 'Detox Health', "Women\'s Balance"],
    advantages: ['Strong natural herbal feel', 'High visual appeal', 'Suitable for brand story marketing', 'Intuitive ingredient presentation'],
    note: 'Tea bag formats require specific particle size and uniform filling. Dissolution rate of active ingredients in hot water must be evaluated in advance.',
    img: 'https://readdy.ai/api/search-image?query=herbal%20wellness%20tea%20bag%20supplement%20dried%20flowers%20chamomile%20herbs%20botanical%20on%20white%20background%2C%20health%20tea%20product%20photography%20natural%20organic%20ingredients&width=600&height=380&seq=df-tea-01&orientation=landscape',
  },
  {
    id: 'liquid', icon: 'ri-water-percent-line', title: 'Liquid', subtitle: 'Fast Absorption, Ideal for Specific Demographics', color: '#b3e5fc', colorDark: '#0277bd',
    desc: 'Liquid dosage forms include oral liquids, functional drinks, and drops. Active ingredients dissolved in liquid absorb quickly — especially suitable for infants, seniors, or those with swallowing difficulties.',
    process: 'Ingredient dissolution & preparation → Sterilization → Filling & sealing (bottle/ampoule) → QC sterilization validation → Dispatch',
    suitable: ['Infant Supplements', 'Senior Health', 'Functional Drinks', 'Collagen Drinks', 'Quick-Boost Beverages'],
    advantages: ['Fastest absorption rate', 'Suitable for special-needs demographics', 'Flavor customization available', 'High product visual identification'],
    note: 'Liquid manufacturing has the strictest microbial control requirements. Filling environment must meet food-grade standards, and shelf life requires thorough validation.',
    img: 'https://readdy.ai/api/search-image?query=liquid%20health%20supplement%20drink%20bottle%20collagen%20beverage%20oral%20solution%20small%20bottle%20with%20natural%20fruit%20ingredients%20on%20clean%20white%20background%2C%20health%20drink%20product%20photography&width=600&height=380&seq=df-liquid-01&orientation=landscape',
  },
];

function DosageFormsPage() {
  const [openDetail, setOpenDetail] = useState<string | null>(null);
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=diverse%20health%20supplement%20dosage%20forms%20capsules%20tablets%20powder%20jelly%20drink%20arranged%20together%20on%20clean%20white%20surface%2C%20professional%20pharmaceutical%20product%20display%20photography%20natural%20light&width=1920&height=420&seq=df-hero-bg-01&orientation=landscape"
            alt="Dosage Forms"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/65 to-[#1a3a1a]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb current="Dosage Forms" />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">OEM/ODM Services</span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">Dosage Forms</h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              Based on the characteristics of different formula ingredients and product positioning, choosing the right dosage form makes nutrition more accessible to your consumers.
            </p>
          </div>
        </div>
      </section>

      {/* Overview pills */}
      <section className="bg-white border-b border-gray-100 py-5">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-gray-400 text-[0.875rem] mr-2">Quick Jump:</span>
            {dosageForms.map((d) => (
              <a key={d.id} href={`#df-${d.id}`} className="whitespace-nowrap flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[0.875rem] font-medium bg-[#f7faf7] text-gray-600 border border-gray-200 hover:border-green-300 hover:text-[#4CAF50] transition-all duration-200 cursor-pointer">
                <i className={`${d.icon} text-sm`} />
                {d.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Dosage forms list */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 space-y-8">
          {dosageForms.map((d) => (
            <div id={`df-${d.id}`} key={d.id} className="scroll-mt-24 bg-white rounded-2xl overflow-hidden border border-gray-100">
              <div className="flex flex-col lg:flex-row">
                <div className="w-full lg:w-[320px] flex-shrink-0">
                  <img src={d.img} alt={d.title} className="w-full h-[260px] lg:h-full object-cover object-top" />
                </div>
                <div className="flex-1 p-7">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${d.colorDark}15` }}>
                        <i className={`${d.icon} text-xl`} style={{ color: d.colorDark }} />
                      </div>
                      <div>
                        <h2 className="text-[#1a2e1a] font-bold text-[1.375rem] leading-tight">{d.title}</h2>
                        <p style={{ color: d.colorDark }} className="text-[0.875rem] font-medium">{d.subtitle}</p>
                      </div>
                    </div>
                    <Link to="/en/contact/form" className="whitespace-nowrap hidden sm:inline-flex items-center gap-1.5 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.8125rem] px-4 py-2 rounded-full transition-all duration-200 cursor-pointer flex-shrink-0">
                      Inquire
                    </Link>
                  </div>
                  <p className="text-gray-600 text-[0.9375rem] leading-[1.75] mb-4">{d.desc}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {d.suitable.map((s) => (
                      <span key={s} className="text-[0.75rem] font-medium px-2.5 py-1 rounded-full border" style={{ color: d.colorDark, borderColor: `${d.colorDark}30`, backgroundColor: `${d.colorDark}08` }}>{s}</span>
                    ))}
                  </div>
                  <button onClick={() => setOpenDetail(openDetail === d.id ? null : d.id)} className="flex items-center gap-2 text-[#4CAF50] font-medium text-[0.875rem] cursor-pointer hover:text-[#43a047] transition-colors duration-200">
                    <i className={`${openDetail === d.id ? 'ri-subtract-line' : 'ri-add-line'} text-base`} />
                    {openDetail === d.id ? 'Collapse Details' : 'View Process & Advantages'}
                  </button>
                  {openDetail === d.id && (
                    <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-[#f7faf7] rounded-xl p-4">
                        <p className="text-[#1a2e1a] font-bold text-[0.875rem] mb-2 flex items-center gap-2">
                          <i className="ri-settings-3-line text-[#4CAF50]" />Process Overview
                        </p>
                        <p className="text-gray-600 text-[0.8125rem] leading-[1.75]">{d.process}</p>
                      </div>
                      <div className="bg-[#f7faf7] rounded-xl p-4">
                        <p className="text-[#1a2e1a] font-bold text-[0.875rem] mb-2 flex items-center gap-2">
                          <i className="ri-star-line text-[#4CAF50]" />Dosage Form Advantages
                        </p>
                        <ul className="space-y-1.5">
                          {d.advantages.map((a) => (
                            <li key={a} className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-[#4CAF50] text-sm flex-shrink-0 mt-0.5" />
                              <span className="text-gray-600 text-[0.8125rem] leading-[1.65]">{a}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="md:col-span-2 bg-amber-50 border border-amber-100 rounded-xl p-4">
                        <p className="text-[#1a2e1a] font-bold text-[0.875rem] mb-1.5 flex items-center gap-2">
                          <i className="ri-information-line text-amber-500" />Important Notes
                        </p>
                        <p className="text-gray-600 text-[0.8125rem] leading-[1.75]">{d.note}</p>
                      </div>
                      <div className="md:col-span-2">
                        <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.9375rem] px-7 py-3 rounded-full transition-all duration-300 cursor-pointer">
                          Inquire About This Form
                          <i className="ri-arrow-right-line" />
                        </Link>
                      </div>
                    </div>
                  )}
                  <div className="mt-4 sm:hidden">
                    <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-1.5 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-5 py-2.5 rounded-full transition-all duration-200 cursor-pointer">
                      Inquire
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">Unsure Which Dosage Form Fits Your Product?</h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">Kang Yong&apos;s R&D team can provide professional dosage form recommendations based on your formula characteristics, target consumers, and market positioning.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              Free Dosage Form Consultation
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/en/services/hot-formulas" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              Browse Best-Selling Formulas
            </Link>
          </div>
        </div>
      </section>
      <ServiceSubNav current="/en/services/dosage-forms" />
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-5">Related Pages</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: 'Best-Selling Formulas', desc: 'Explore 17 market-proven formula directions paired with the most suitable dosage forms.', href: '/en/services/hot-formulas' },
              { title: 'Packaging Design', desc: 'Once the dosage form is confirmed, choose the packaging that best represents your brand identity.', href: '/en/services/packaging' },
              { title: 'Contact Us', desc: 'Fill out our inquiry form and let Kang Yong help you select the ideal dosage form.', href: '/en/contact/form' },
            ].map((r) => (
              <Link key={r.href} to={r.href} className="block bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer group">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5 group-hover:text-[#4CAF50] transition-colors">{r.title}</p>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{r.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────
// Packaging Page
// ─────────────────────────────────────────────
const processSteps = [
  { num: '01', icon: 'ri-discuss-line', title: 'Needs Assessment', desc: 'Confirm brand style, target demographics, channel requirements, and packaging specification direction.' },
  { num: '02', icon: 'ri-palette-line', title: 'Initial Draft', desc: 'Designer provides 2–3 sets of packaging visual draft proposals for selection.' },
  { num: '03', icon: 'ri-printer-line', title: 'Print Sample', desc: 'After confirming the design, create a print proof to verify actual colors and texture.' },
  { num: '04', icon: 'ri-edit-line', title: 'Design Refinement', desc: 'Adjust color deviations, typography, and layout details based on proof results.' },
  { num: '05', icon: 'ri-search-eye-line', title: 'Final Proofreading', desc: 'Final regulatory label content review, ensuring ingredients, label format comply with regulations.' },
  { num: '06', icon: 'ri-send-plane-line', title: 'Send to Print', desc: 'Once the final version is confirmed, send to the printing factory for mass production and on-schedule delivery.' },
];

const packagingTypes = [
  {
    id: 'box', icon: 'ri-archive-line', title: 'Box Types',
    desc: 'The outer box is the first impression of your brand. Kang Yong offers multiple box styles — from top-lid boxes, magnetic closure boxes to drawer-style boxes — matched to your product positioning and price point.',
    materials: 'Paperboard (grey board / white board / kraft), with surface options of matte film / gloss film / foil stamping / embossing',
    suitable: ['Capsule Bottle Gift Boxes', 'Gift Set Combinations', 'Premium Brand Packaging', 'Limited Edition Seasonal Packaging'],
    img: 'https://readdy.ai/api/search-image?query=premium%20health%20supplement%20product%20box%20packaging%20cardboard%20white%20minimal%20clean%20design%20elegant%20brand%20packaging%20mockup%20on%20white%20background%20professional%20photography&width=600&height=380&seq=pkg-box-01&orientation=landscape',
  },
  {
    id: 'bottle', icon: 'ri-flask-line', title: 'Bottle & Container Types',
    desc: 'Bottles and containers are the most common primary packaging for capsules, tablets, and powder products. Options include HDPE plastic bottles, PET transparent bottles, glass bottles, or aluminum cans — each offering different advantages for moisture protection, light blocking, and aesthetics.',
    materials: 'HDPE plastic bottles (white/colored), PET transparent bottles, glass bottles, aluminum cans',
    suitable: ['Capsule / Tablet Filling', 'Protein Powder', 'Premium Formula Products', 'Vegetarian-Certified Products'],
    img: 'https://readdy.ai/api/search-image?query=various%20supplement%20bottles%20HDPE%20PET%20glass%20amber%20brown%20white%20bottle%20types%20for%20capsules%20tablets%20powder%20on%20clean%20white%20background%2C%20pharmaceutical%20packaging%20product%20photography&width=600&height=380&seq=pkg-bottle-01&orientation=landscape',
  },
  {
    id: 'foil', icon: 'ri-file-paper-2-line', title: 'Foil Packaging',
    desc: 'Aluminum foil stand-up pouches or stick packs are the primary packaging choice for powder, granule, and jelly-type products. Aluminum foil provides excellent moisture and light protection — especially suitable for moisture-sensitive ingredients like probiotics.',
    materials: 'Aluminum foil stand-up pouches (zip lock / no zip lock), aluminum foil stick packs (filled by stick pack machine)',
    suitable: ['Powder Supplements', 'Probiotic Formulas', 'Jelly Sticks', 'Instant Tea Drinks'],
    img: 'https://readdy.ai/api/search-image?query=aluminum%20foil%20pouch%20sachet%20packaging%20for%20health%20supplement%20powder%20probiotic%20on%20clean%20white%20background%2C%20metallic%20silver%20flexible%20packaging%20product%20photography&width=600&height=380&seq=pkg-foil-01&orientation=landscape',
  },
  {
    id: 'ptp', icon: 'ri-layout-grid-line', title: 'PTP Blister Packaging',
    desc: 'PTP (Press Through Package) blister packs are a premium packaging option for tablets and capsules. Each unit is individually sealed for excellent moisture protection. Suitable for high-price-point brands seeking premium quality feel, commonly seen in Japan and European market products.',
    materials: 'PVC/PVDC bottom film + aluminum foil top seal, with customizable print colors and specifications',
    suitable: ['Premium Brand Positioning', 'Japanese-Style Quality Products', 'Pharmacy Channel Products', 'Export to Japan/Korea'],
    img: 'https://readdy.ai/api/search-image?query=PTP%20blister%20pack%20pharmaceutical%20supplement%20capsule%20tablet%20blister%20packaging%20silver%20aluminum%20press%20through%20package%20on%20white%20background%2C%20high%20end%20health%20product%20packaging%20photography&width=600&height=380&seq=pkg-ptp-01&orientation=landscape',
  },
];

function PackagingPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20health%20supplement%20product%20packaging%20design%20collection%20various%20boxes%20bottles%20pouches%20on%20clean%20white%20background%2C%20professional%20brand%20packaging%20display%20photography%20minimal&width=1920&height=420&seq=pkg-hero-bg-01&orientation=landscape"
            alt="Packaging Design"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/65 to-[#1a3a1a]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb current="Packaging Design" />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">OEM/ODM Services</span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">Packaging Design</h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              From packaging material selection and visual design to regulatory labeling — helping brands create complete, distinctive health supplement packaging.
            </p>
          </div>
        </div>
      </section>

      {/* Design process */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">Design Process</span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">6-Step Packaging Design Process</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">From needs assessment to final print dispatch — every step is overseen by professionals to ensure a perfect final result</p>
          </div>

          {/* Desktop horizontal flow */}
          <div className="hidden lg:grid grid-cols-6 gap-0 mb-10">
            {processSteps.map((s, idx) => (
              <div key={s.num} className="relative flex flex-col items-center">
                {idx < processSteps.length - 1 && (
                  <div className="absolute top-9 left-1/2 w-full h-0.5 bg-green-200 z-0" />
                )}
                <div className="relative z-10 flex flex-col items-center px-3 w-full">
                  <div className="w-[72px] h-[72px] flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white mb-4 relative">
                    <i className={`${s.icon} text-[#4CAF50] text-2xl`} />
                    <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#4CAF50] text-white text-[0.625rem] font-bold rounded-full">{s.num}</span>
                  </div>
                  <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5 text-center">{s.title}</h3>
                  <p className="text-gray-500 text-[0.8125rem] leading-[1.65] text-center">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile vertical flow */}
          <div className="lg:hidden space-y-0">
            {processSteps.map((s, idx) => (
              <div key={s.num} className="flex gap-4">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white relative flex-shrink-0">
                    <i className={`${s.icon} text-[#4CAF50] text-xl`} />
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-[#4CAF50] text-white text-[0.625rem] font-bold rounded-full">{s.num}</span>
                  </div>
                  {idx < processSteps.length - 1 && (
                    <div className="w-0.5 flex-1 bg-green-200 my-2 min-h-[32px]" />
                  )}
                </div>
                <div className="pb-6 flex-1 min-w-0">
                  <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-1">{s.title}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-white rounded-2xl border border-green-100 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-xl flex-shrink-0 border border-green-100">
              <i className="ri-information-line text-[#4CAF50] text-xl" />
            </div>
            <div>
              <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">Regulatory Label Review Cannot Be Skipped</p>
              <p className="text-gray-500 text-[0.875rem] leading-[1.75]">Before finalizing packaging, Kang Yong will help review mandatory label items including ingredient declarations, weight statements, and storage instructions to ensure compliance with Taiwan\'s Food Safety Management Act before launch.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Packaging types */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">Packaging Types</span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">Diverse Packaging Material Options</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">Choose the most suitable packaging combination based on product positioning, dosage form characteristics, and target market</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            {packagingTypes.map((p) => (
              <div key={p.id} className="bg-[#f7faf7] rounded-2xl overflow-hidden border border-gray-100 hover:border-green-200 transition-all duration-300">
                <div className="w-full h-[260px] overflow-hidden">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-white rounded-xl border border-green-100">
                      <i className={`${p.icon} text-[#4CAF50] text-lg`} />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.125rem]">{p.title}</h3>
                  </div>
                  <p className="text-gray-600 text-[0.9375rem] leading-[1.75] mb-4">{p.desc}</p>
                  <div className="bg-white rounded-xl p-3.5 mb-4 border border-gray-100">
                    <p className="text-[#1a2e1a] font-semibold text-[0.8125rem] mb-1 flex items-center gap-1.5">
                      <i className="ri-tools-line text-[#4CAF50] text-sm" />Common Materials
                    </p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{p.materials}</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {p.suitable.map((s) => (
                      <span key={s} className="bg-green-50 border border-green-100 text-[#4CAF50] text-[0.75rem] px-2.5 py-1 rounded-full font-medium">{s}</span>
                    ))}
                  </div>
                  <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-1.5 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[0.875rem] px-5 py-2.5 rounded-full transition-all duration-200 cursor-pointer">
                    Inquire About Packaging
                    <i className="ri-arrow-right-line text-sm" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Label compliance */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10 items-center">
            <div className="w-full lg:w-1/2">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">Label Compliance</span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.625rem] leading-tight mb-5">
                Packaging Is Not Just About Looks —<br />Label Compliance Matters Just as Much
              </h2>
              <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">
                Taiwan's food labeling regulations clearly require packaging to display all mandatory information. Non-compliant labeling may lead to product recalls or fines.
              </p>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full min-w-[500px] text-sm">
                  <thead>
                    <tr className="bg-[#f7faf7] border-b border-gray-200">
                      <th className="text-left px-4 py-3 text-[#1a2e1a] font-bold text-[0.875rem] w-8">#</th>
                      <th className="text-left px-4 py-3 text-[#1a2e1a] font-bold text-[0.875rem]">Required Label Item</th>
                      <th className="text-left px-4 py-3 text-[#1a2e1a] font-bold text-[0.875rem]">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['1', 'Product Name', 'Clear product name that accurately reflects the nature of the product'],
                      ['2', 'Ingredient List', 'All ingredients listed in descending order by content weight'],
                      ['3', 'Net Weight / Volume', 'Expressed in metric units (g, mg, ml, L)'],
                      ['4', 'Expiry Date', 'Clearly marked best-before or expiry date with storage instructions'],
                      ['5', 'Manufacturer Information', 'Full company name, address, and contact phone number'],
                      ['6', 'Country of Origin', 'Explicitly indicate country of manufacture or origin'],
                      ['7', 'Nutrition Labeling', 'Per-serving and per-100g/100ml nutritional values where applicable'],
                      ['8', 'Serving Size & Directions', 'Recommended daily intake and method of consumption'],
                      ['9', 'Additive Declarations', 'All food additives with functional class and name'],
                      ['10', 'Allergen Warnings', 'Mandatory allergen declarations as required by regulation'],
                    ].map((row) => (
                      <tr key={row[0]} className="border-b border-gray-100 hover:bg-[#fafffe]">
                        <td className="px-4 py-2.5 text-gray-400 text-[0.8125rem]">{row[0]}</td>
                        <td className="px-4 py-2.5 text-[#1a2e1a] font-medium text-[0.875rem]">{row[1]}</td>
                        <td className="px-4 py-2.5 text-gray-600 text-[0.8125rem] leading-[1.65]">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=health%20supplement%20product%20label%20design%20packaging%20close%20up%20nutrition%20facts%20ingredient%20list%20regulatory%20compliance%20label%20printing%20on%20clean%20white%20bottle%2C%20professional%20product%20label%20photography&width=720&height=460&seq=pkg-label-01&orientation=landscape"
                  alt="Label Compliance"
                  className="w-full h-[320px] lg:h-[380px] object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            Let Kang Yong Help You Create the Perfect Packaging Solution
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            From material selection to label review — one-stop packaging design services to make your product stand out on the shelf.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              Inquire About Packaging Solutions
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/en/cooperation/process" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              Learn About Our Cooperation Process
            </Link>
          </div>
        </div>
      </section>
      <ServiceSubNav current="/en/services/packaging" />
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-5">Related Pages</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: 'Dosage Forms', desc: 'After confirming the dosage form, select the most complementary packaging to complete your brand presentation.', href: '/en/services/dosage-forms' },
              { title: 'Regulatory Risks', desc: 'Packaging label regulatory requirements — let Kang Yong ensure every detail is compliant.', href: '/en/about/regulation' },
              { title: 'Contact Us', desc: 'Fill out our inquiry form and let Kang Yong help you create the ideal packaging design.', href: '/en/contact/form' },
            ].map((r) => (
              <Link key={r.href} to={r.href} className="block bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer group">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5 group-hover:text-[#4CAF50] transition-colors">{r.title}</p>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{r.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────
// Router
// ─────────────────────────────────────────────
export default function EnServicesSubPage() {
  const location = useLocation();
  const path = location.pathname;

  const seoDataMap: Record<string, { title: string; description: string; keywords: string }> = {
    '/en/services/hot-formulas': {
      title: 'Best-Selling Formulas | 17 Formula Types | Kang Yong Biotech',
      description: 'Kang Yong Biotech offers 17+ proven health supplement formula directions: natural beauty, women health, men vitality, gut health, eye clarity, sports, sleep, bone, hair, kids, baby, weight, daily nutrition, pet, focus, immune, probiotic.',
      keywords: 'best-selling formulas, 17 formula types, health supplement ODM, beauty formula, sports nutrition, probiotic formula',
    },
    '/en/services/natural-ingredients': {
      title: 'Natural Ingredients | Animal, Plant, Mushroom, Algae & Biochemical | Kang Yong',
      description: 'Explore Kang Yong Biotech natural ingredient categories: animal-derived (collagen, fish oil), plant-derived (curcumin, cranberry), mushroom-derived (reishi), algae & oils, biochemical (CoQ10, GABA), and nutritional sources.',
      keywords: 'natural ingredients, health supplement raw materials, plant extract, animal derived, mushroom supplement, algae DHA, CoQ10',
    },
    '/en/services/dosage-forms': {
      title: 'Dosage Forms | Powder, Tablet, Capsule, Jelly, Tea, Liquid | Kang Yong Biotech',
      description: 'Kang Yong Biotech dosage forms: powder (fast dissolving), tablet (convenient), capsule (taste masking), jelly stick, tea bag, and liquid (fast absorption) — 6 forms for OEM/ODM manufacturing.',
      keywords: 'dosage forms, powder supplement, tablet manufacturing, capsule OEM, jelly stick, tea bag, liquid supplement',
    },
    '/en/services/packaging': {
      title: 'Packaging Design | Box, Bottle, Foil, PTP & Label Compliance | Kang Yong Biotech',
      description: 'Kang Yong Biotech packaging design: 6-step design process, box types, bottles, foil pouches, PTP blister packaging, and mandatory label compliance review for health supplements.',
      keywords: 'packaging design, health supplement packaging, box bottle foil, PTP blister, label compliance, packaging OEM',
    },
  };

  const currentSeo = seoDataMap[path] || seoDataMap['/en/services/hot-formulas'];
  useSEO({ ...currentSeo, lang: 'en', canonicalPath: path });

  return (
    <EnLayout>
      {path === '/en/services/hot-formulas' && <HotFormulasPage />}
      {path === '/en/services/natural-ingredients' && <NaturalIngredientsPage />}
      {path === '/en/services/dosage-forms' && <DosageFormsPage />}
      {path === '/en/services/packaging' && <PackagingPage />}
    </EnLayout>
  );
}
